import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import AdminLoading from './AdminLoading';
import { IoDocumentTextOutline, IoInformationCircleOutline } from 'react-icons/io5';

/** Storefront landing pages that use shop-context hero sliders (not page-context). */
const SHOP_CONTEXT_SLUGS = new Set(['festive-vibes', 'occasions', 'shop']);

const PAGE_PATHS = {
  'home': '/',
  'shop': '/shop',
  'blog': '/blog',
  'festive-vibes': '/festive-vibes',
  'occasions': '/occasions',
  'about': '/about',
  'contact': '/contact',
  'terms': '/terms',
  'privacy-policy': '/privacy-policy',
  'shipping-policy': '/shipping-policy',
  'refund-policy': '/refund-policy',
  'cookie-policy': '/cookie-policy',
};

export default function AdminPages() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createSlug, setCreateSlug] = useState('');
  const [createTitle, setCreateTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const load = () => {
    api('/api/admin/pages')
      .then(setList)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleCreateOpen = () => {
    setCreateSlug('');
    setCreateTitle('');
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    const slug = createSlug.trim();
    const title = createTitle.trim();
    if (!slug || !title) return;
    setCreating(true);
    setError('');
    try {
      const data = await api('/api/admin/pages', { method: 'POST', body: JSON.stringify({ slug, title, content: [] }) });
      const s = data.slug || slug;
      setList((prev) => [...prev, { slug: s, title }]);
      setCreateOpen(false);
      navigate(`/admin/pages/edit/${encodeURIComponent(s)}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"?`)) return;
    try {
      await api(`/api/admin/pages/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      setList((prev) => prev.filter((p) => p.slug !== slug));
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <AdminLoading />;

  const cmsPages = list.filter((p) => !p.is_system);
  const systemPages = list.filter((p) => p.is_system);

  return (
    <>
      {/* ── CMS Pages ─────────────────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="CMS Pages"
          subheader="Pages whose content is fully managed from the admin (e.g. Home). Edit blocks, layout, and hero banners."
          action={
            <Button variant="contained" size="small" onClick={handleCreateOpen}>
              Create new page
            </Button>
          }
        />
        <CardContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          {cmsPages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No CMS pages yet. Create one above or seed the home page.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cmsPages.map((p) => (
                    <TableRow key={p.slug} hover>
                      <TableCell><strong>{p.title || p.slug}</strong></TableCell>
                      <TableCell>
                        <a href={PAGE_PATHS[p.slug] || `/${p.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                          {PAGE_PATHS[p.slug] || `/${p.slug}`}
                        </a>
                      </TableCell>
                      <TableCell>{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '–'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => navigate(`/admin/pages/edit/${encodeURIComponent(p.slug)}`)}>
                          Edit content
                        </Button>
                        <Button
                          size="small"
                          component={RouterLink}
                          to="/admin/pages/hero-sliders"
                          sx={{ ml: 1 }}
                        >
                          Hero slider
                        </Button>
                        {p.slug !== 'home' && (
                          <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => handleDelete(p.slug)}>
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── System (React-coded) Pages ────────────────────────────────────── */}
      <Card>
        <CardHeader
          title="Storefront pages"
          subheader="Edit content blocks for these pages while their functional sections, such as catalogs and forms, remain in place."
        />
        <CardContent>
          <Alert severity="info" icon={<IoInformationCircleOutline />} sx={{ mb: 2, fontSize: '0.82rem' }}>
            To edit <strong>meta title / description</strong> for these pages go to{' '}
            <RouterLink to="/admin/settings/seo" style={{ color: 'inherit', fontWeight: 600 }}>Settings → SEO → Page SEO tab</RouterLink>.
            To add <strong>hero banners</strong> go to{' '}
            <RouterLink to="/admin/pages/hero-sliders" style={{ color: 'inherit', fontWeight: 600 }}>Hero Sliders</RouterLink>.
          </Alert>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Quick links</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {systemPages.map((p) => (
                  <TableRow key={p.slug} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IoDocumentTextOutline size={16} />
                        <strong>{p.title || p.slug}</strong>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <a href={PAGE_PATHS[p.slug] || `/${p.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                        {PAGE_PATHS[p.slug] || `/${p.slug}`}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="This page keeps its built-in functionality and accepts managed content blocks.">
                        <Chip label="Managed system page" size="small" variant="outlined" color="default" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate(`/admin/pages/edit/${encodeURIComponent(p.slug)}`)}
                        sx={{ mr: 1 }}
                      >
                        Edit content
                      </Button>
                      <Button
                        size="small"
                        component={RouterLink}
                        to="/admin/settings/seo"
                        sx={{ mr: 1 }}
                      >
                        Edit SEO
                      </Button>
                      <Button
                        size="small"
                        component={RouterLink}
                        to="/admin/pages/hero-sliders"
                      >
                        {SHOP_CONTEXT_SLUGS.has(p.slug) ? 'Hero slider →' : 'Hero slider'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create CMS page</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Creates a new page at <code>/your-slug</code> with a block editor. Use this for custom landing pages, not for existing React-coded pages.
          </Typography>
          <TextField
            size="small"
            label="Page slug"
            placeholder="e.g. offers"
            value={createSlug}
            onChange={(e) => setCreateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            size="small"
            label="Page title"
            placeholder="e.g. Special Offers"
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={!createSlug.trim() || !createTitle.trim() || creating}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
